import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
// ASUME: Necesitas crear esta función en tu movimientoService
import { obtenerDetalleFactura } from "../services/facturaService";

/*
 * ========================================
 * 2. HELPERS Y FORMATTERS
 * ========================================
 */
const formatDate = (dateString) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleDateString("es-MX", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
  } catch {
    return String(dateString).slice(0, 10);
  }
};

const formatCurrency = (n, isDollar = false) =>
  (Number(n) || 0).toLocaleString("es-MX", {
    style: "currency",
    currency: isDollar ? "USD" : "MXN",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

/*
 * ========================================
 * 3. COMPONENTE DE DETALLE
 * ========================================
 */

export default function FacturaDetallePage() {
  // Obtener los parámetros de la URL: /factura/detalle/:serie/:factura/:empresa
  const { serie, factura, empresa } = useParams();

  const [detalle, setDetalle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancel = false; // Variable de control de limpieza/cancelación

    const loadFactura = async () => {
      // Ponemos el estado de carga en true al inicio
      setLoading(true);
      setError(null);

      try {
        // Aquí usamos la función del servicio real (asumo que se llama 'obtenerDetalleFactura')
        const result = await obtenerDetalleFactura({
          serie,
          // Asegúrate de parsear a número si vienen como string de useParams
          factura: parseInt(factura),
          empresa: parseInt(empresa),
        });

        // Solo actualiza el estado si el componente no se ha desmontado
        if (!cancel) {
          setDetalle(result); // Guarda los datos
        }
      } catch (error) {
        console.error("Error al cargar el detalle de la factura:", error);

        // Solo actualiza el estado si el componente no se ha desmontado
        if (!cancel) {
          // Muestra el mensaje de error del servicio
          setError(
            error.message || "No se pudo cargar el detalle de la factura.",
          );
        }
      } finally {
        // Solo actualiza el estado si el componente no se ha desmontado
        if (!cancel) {
          setLoading(false);
        }
      }
    };

    loadFactura();

    // Función de limpieza de useEffect: se ejecuta al desmontar el componente
    // o antes de que el efecto se ejecute de nuevo.
    return () => {
      cancel = true; // Marca la operación asíncrona como cancelada
    };
  }, [serie, factura, empresa]); // Dependencias: recargar si estos valores cambian

  // Render de estados de carga y error
  if (loading)
    return (
      <div style={{ padding: 16 }}>
        Cargando Detalle de Factura {serie}-{factura}...
      </div>
    );
  if (error)
    return <div style={{ padding: 16, color: "crimson" }}>{error}</div>;
  if (!detalle)
    return (
      <div style={{ padding: 16 }}>
        Factura {serie}-{factura} no encontrada.
      </div>
    );

  // Lógica de visualización
  const isDollar = detalle.moneda === "D";
  const currencyFormat = (n) => formatCurrency(n, isDollar);

  return (
    <div style={{ padding: 16, maxWidth: 1280, margin: "0 auto" }}>
      <h2 style={{ marginTop: 16, marginBottom: 4 }}>
        Detalle de Factura{" "}
        <span style={{ color: "#007bff" }}>
          {detalle.serie}-{detalle.numeroDocumento}
        </span>
      </h2>

      {/* SECCIÓN DE CABECERA */}
      <section style={card}>
        <h3 style={sectionTitle}>Datos de Cabecera</h3>
        <div style={headerGrid}>
          <DetailItem label="Cliente" value={detalle.claveCliente} />
          <DetailItem label="Nombre" value={detalle.nombreCliente} span={3} />
          <DetailItem
            label="Fecha Factura"
            value={formatDate(detalle.fechaMovimiento)}
          />
          <DetailItem
            label="Condición Pago"
            value={detalle.condicionesPago || "N/A"}
          />
          <DetailItem label="RFC" value={detalle.rfc || "N/A"} />
          <DetailItem
            label="Moneda"
            value={isDollar ? "Dólares (USD)" : "Pesos (MXN)"}
          />
        </div>
      </section>

      {/* SECCIÓN DE PARTIDAS (DETALLE) */}
      <section style={{ ...card, marginTop: 20 }}>
        <h3 style={sectionTitle}>Partidas / Artículos</h3>
        <div style={{ overflowX: "auto" }}>
          <table style={tableAuto}>
            <thead>
              <tr style={{ background: "#f5f5f5" }}>
                <th style={th}>Clave</th>
                <th style={th}>Descripción</th>
                <th style={thNum}>Cant.</th>
                <th style={th}>Unidad</th>
                <th style={thNum}>Precio</th>
                <th style={thNum}>Importe</th>
              </tr>
            </thead>
            <tbody>
              {(detalle.partidas || []).map((partida, idx) => (
                <tr
                  key={idx}
                  style={{ background: idx % 2 === 0 ? "#fafafa" : "none" }}
                >
                  <td style={td}>{partida.claveArticulo}</td>
                  <td style={{ ...td, minWidth: 200 }}>
                    {partida.descripcion}
                  </td>
                  <td style={tdNum}>
                    {Number(partida.cantidad).toLocaleString("es-MX")}
                  </td>
                  <td style={td}>{partida.unidad}</td>
                  <td style={tdNum}>{currencyFormat(partida.precio)}</td>
                  <td style={{ ...tdNum, fontWeight: "bold" }}>
                    {currencyFormat(partida.importe)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td
                  colSpan="5"
                  style={{ ...tdTotal, textAlign: "right", fontWeight: 600 }}
                >
                  TOTAL FACTURA:
                </td>
                <td
                  style={{ ...tdTotal, fontWeight: "bold", color: "#007bff" }}
                >
                  {currencyFormat(detalle.importeTotal)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* SECCIÓN DE RESUMEN Y ESTADO */}
      <section style={{ ...card, marginTop: 20 }}>
        <h3 style={sectionTitle}>Resumen y Estado</h3>
        <div style={summaryGrid}>
          <SummaryBox
            label="Importe Total"
            value={currencyFormat(detalle.importeTotal)}
            color="#007bff"
          />
          <SummaryBox
            label="Saldo Pendiente"
            value={currencyFormat(detalle.saldoPendiente)}
            color={detalle.saldoPendiente > 0 ? "crimson" : "green"}
          />
          <SummaryBox
            label="Estado"
            value={detalle.saldoPendiente > 0 ? "PENDIENTE" : "PAGADA"}
            color={detalle.saldoPendiente > 0 ? "#f0ad4e" : "#5cb85c"}
          />
        </div>
      </section>
    </div>
  );
}

/*
 * ========================================
 * 4. COMPONENTES DE ESTRUCTURA Y ESTILOS
 * ========================================
 */

// Componente para una fila de detalle
const DetailItem = ({ label, value, span = 1 }) => (
  <div style={{ gridColumn: `span ${span}` }}>
    <p
      style={{
        margin: 0,
        fontSize: 12,
        color: "#999",
        textTransform: "uppercase",
      }}
    >
      {label}
    </p>
    <p style={{ margin: "2px 0 8px 0", fontWeight: 500, color: "#333" }}>
      {value}
    </p>
  </div>
);

// Componente para el resumen (cajas grandes)
const SummaryBox = ({ label, value, color }) => (
  <div
    style={{
      ...card,
      padding: 12,
      textAlign: "center",
      borderTop: `4px solid ${color}`,
    }}
  >
    <p
      style={{
        margin: 0,
        fontSize: 14,
        color: "#666",
        textTransform: "uppercase",
      }}
    >
      {label}
    </p>
    <h4 style={{ margin: "8px 0 0 0", fontSize: 24, color: color }}>{value}</h4>
  </div>
);

// Estilos
const card = {
  background: "#fff",
  borderRadius: 8,
  boxShadow: "0 1px 4px rgba(0,0,0,.1)",
  padding: 16,
};
const sectionTitle = {
  margin: "0 0 16px 0",
  borderBottom: "1px solid #eee",
  paddingBottom: 8,
  color: "#333",
};
const headerGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(4, 1fr)",
  gap: "0 20px",
};
const summaryGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))",
  gap: "20px",
};

const tableAuto = {
  width: "100%",
  borderCollapse: "collapse",
  tableLayout: "auto",
};
const th = {
  textAlign: "left",
  padding: "10px 12px",
  borderBottom: "2px solid #ccc",
  fontSize: 14,
  background: "#e9e9e9",
  color: "#333",
  fontWeight: 700,
};
const thNum = { ...th, textAlign: "right" };
const td = {
  padding: "8px 12px",
  borderBottom: "1px solid #f1f1f1",
  fontSize: 13,
  verticalAlign: "top",
};
const tdNum = { ...td, textAlign: "right", fontVariantNumeric: "tabular-nums" };
const tdTotal = { ...td, borderTop: "2px solid #ccc", padding: "12px 12px" };
